/**
 * ComplaintsPanel
 *
 * Admin → "Complaints" tab. Shows every logged complaint. Filters by
 * status + attended-by + free-text search. Inline editing via the same
 * ComplaintDialog the dashboard opens — mirrors the "open the same
 * dialog instead" pattern that lives elsewhere in the admin area.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Search, AlertCircle, RefreshCw, Pencil, Camera, X, Loader2, Eye, Trash2 } from 'lucide-react';
import { api, API, useAuth } from '../../lib/store';
import axios from 'axios';
import { photoUrl, photoUrls, PhotoThumb, useImageViewer } from '../ImageViewer';

export const COMPLAINT_STATUSES = [
  { value: 'pending',     label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'closed',      label: 'Closed' },
];

export const STATUS_LABEL = { pending: 'Pending', in_progress: 'In Progress', closed: 'Closed' };
export const STATUS_CHIP = {
  pending:     'bg-amber-50 border-amber-50 text-amber-700',
  in_progress: 'bg-sky-50 border-sky-50 text-sky-700',
  closed:      'bg-emerald-50 border-emerald-50 text-emerald-700',
};

const MAX_SITE_IMAGES = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch {
    return String(iso).slice(0, 10);
  }
};

export default function ComplaintsPanel() {
  const { can } = useAuth();
  const isAdmin = can('complaints.delete');
  const [complaints, setComplaints] = useState([]);
  const [total, setTotal] = useState(0);
  const [salespeople, setSalespeople] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [attendedByFilter, setAttendedByFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  // Complaint ID for display — existing records have no complaint_number,
  // so we fall back to the first 8 chars of the internal uuid.
  const displayId = (c) => c?.complaint_number || (c?.id ? c.id.slice(0, 8) : '—');

  // Destructive-action confirmation — mirrors the window.confirm pattern
  // used by LeadRegisterPanel.jsx (~L99 handleDeleteLead).
  const handleDeleteComplaint = async (c) => {
    const label = c.complaint_number || (c.id ? c.id.slice(0, 8) : c.customer_name);
    if (!window.confirm(`Delete complaint ${label} for ${c.customer_name}? This cannot be undone.`)) return;
    try {
      await api.deleteComplaint(c.id);
      toast.success('Complaint deleted');
      fetchComplaints();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete complaint');
    }
  };

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (attendedByFilter !== 'all') params.attended_by = attendedByFilter;
      const res = await api.getComplaints(params);
      setComplaints(res.data?.complaints || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.getSalespeople()
      .then((res) => setSalespeople(res.data?.salespeople || []))
      .catch(() => setSalespeople([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => { if (!cancelled) fetchComplaints(); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, attendedByFilter]);

  const onSearchKey = (e) => {
    if (e.key === 'Enter') fetchComplaints();
  };

  return (
    <div className="space-y-4" data-testid="admin-complaints-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-[17px] sm:text-[19px] font-bold text-heda-ink flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-heda-gold" /> Complaints
            <span className="ml-1 rounded-full bg-heda-cream px-2 py-0.5 text-[10px] font-bold tabular-nums text-heda-mute">{total}</span>
          </h2>
          <p className="text-[11.5px] text-heda-mute mt-0.5">
            Every complaint logged from the dashboard. Update the status once resolved; only an admin can delete a complaint.
          </p>
        </div>
        <Button className="h-10 text-[12.5px] rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" size="sm" onClick={fetchComplaints} disabled={loading} data-testid="complaints-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label htmlFor="cx-search" className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Search</Label>
          <div className="relative">
            <Search className="w-4 h-4 text-heda-mute absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              id="cx-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Customer, mobile, staff, notes…"
              className="h-10 rounded-xl pl-9 bg-heda-cream/50"
              data-testid="complaints-search"
            />
          </div>
        </div>
        <div className="w-[calc(50%-0.25rem)] sm:w-40">
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-10 rounded-xl bg-heda-cream/50" data-testid="complaints-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {COMPLAINT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[calc(50%-0.25rem)] sm:w-52">
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Attended By</Label>
          <Select value={attendedByFilter} onValueChange={setAttendedByFilter}>
            <SelectTrigger className="h-10 rounded-xl bg-heda-cream/50" data-testid="complaints-filter-attended"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Anyone</SelectItem>
              {salespeople.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button className="h-10 rounded-xl bg-heda-ink px-4 text-white shadow-none hover:bg-black" size="sm" onClick={fetchComplaints} data-testid="complaints-apply-btn">Apply</Button>
      </div>

      {/* Table */}
      <div className="heda-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="complaints-table">
          <thead className="heda-thead">
            <tr>
              <th className="px-3 sm:px-4 py-2.5 font-semibold hidden lg:table-cell">Complaint ID</th>
              <th className="px-3 sm:px-4 py-2.5 font-semibold hidden sm:table-cell">Date</th>
              <th className="px-3 sm:px-4 py-2.5 font-semibold">Customer</th>
              <th className="px-3 sm:px-4 py-2.5 font-semibold hidden md:table-cell">Mobile</th>
              <th className="px-3 sm:px-4 py-2.5 font-semibold hidden lg:table-cell">Attended By</th>
              <th className="px-3 sm:px-4 py-2.5 font-semibold">Status</th>
              <th className="px-3 sm:px-4 py-2.5 font-semibold hidden lg:table-cell">Description</th>
              <th className="px-3 sm:px-4 py-2.5 font-semibold hidden xl:table-cell">Action Taken</th>
              <th className="px-3 sm:px-4 py-2.5 font-semibold hidden 2xl:table-cell">Logged By</th>
              <th className="px-3 sm:px-4 py-2.5 font-semibold hidden lg:table-cell">Photos</th>
              <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="text-center text-heda-mute py-10"><Loader2 className="inline w-5 h-5 mr-2 animate-spin text-heda-gold" />Loading…</td></tr>
            )}
            {!loading && complaints.length === 0 && (
              <tr><td colSpan={11} className="text-center text-heda-mute py-10">No complaints found.</td></tr>
            )}
            {!loading && complaints.map((c) => (
              <tr key={c.id} className="border-t border-heda-line" data-testid={`complaints-row-${c.id}`}>
                <td className="px-3 sm:px-4 py-3 whitespace-nowrap font-mono text-[11px] text-heda-sub hidden lg:table-cell">{displayId(c)}</td>
                <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-heda-sub hidden sm:table-cell">{fmtDate(c.created_at)}</td>
                <td className="px-3 sm:px-4 py-3">
                  <div className="font-medium text-heda-ink">{c.customer_name}</div>
                  <div className="text-[11px] text-heda-mute tabular-nums md:hidden">{c.mobile || '—'}</div>
                </td>
                <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-heda-sub tabular-nums hidden md:table-cell">{c.mobile || '—'}</td>
                <td className="px-3 sm:px-4 py-3 text-heda-sub hidden lg:table-cell">{c.attended_by_name || '—'}</td>
                <td className="px-3 sm:px-4 py-3">
                  <span className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${STATUS_CHIP[c.status] || STATUS_CHIP.pending}`}>
                    {STATUS_LABEL[c.status] || 'Pending'}
                  </span>
                </td>
                <td className="px-3 sm:px-4 py-3 text-heda-sub max-w-[180px] truncate hidden lg:table-cell" title={c.description}>{c.description || '—'}</td>
                <td className="px-3 sm:px-4 py-3 text-heda-sub max-w-[160px] truncate hidden xl:table-cell" title={c.action_taken}>{c.action_taken || '—'}</td>
                <td className="px-3 sm:px-4 py-3 text-heda-sub hidden 2xl:table-cell">{c.created_by_name || '—'}</td>
                <td className="px-3 sm:px-4 py-3 hidden lg:table-cell">
                  {(c.images || []).length > 0 ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-heda-sub">
                      <Camera className="w-3 h-3" /> {c.images.length}
                    </span>
                  ) : (
                    <span className="text-[11px] text-heda-mute">—</span>
                  )}
                </td>
                <td className="px-3 sm:px-4 py-3 text-right whitespace-nowrap">
                  <Button className="h-9 min-w-9 rounded-full px-2 2xl:px-3 hover:bg-heda-cream"
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewing(c)}
                    title="View"
                    data-testid={`complaints-view-${c.id}`}
                  >
                    <Eye className="w-4 h-4" /><span className="hidden 2xl:inline">View</span>
                  </Button>
                  <Button className="h-9 min-w-9 rounded-full px-2 2xl:px-3 hover:bg-heda-cream"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing(c)}
                    title="Edit"
                    data-testid={`complaints-edit-${c.id}`}
                  >
                    <Pencil className="w-4 h-4" /><span className="hidden 2xl:inline">Edit</span>
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteComplaint(c)}
                      className="h-9 min-w-9 rounded-full px-2 2xl:px-3 text-red-700 hover:text-red-700 hover:bg-red-50"
                      title="Delete"
                      data-testid={`complaints-delete-${c.id}`}
                    >
                      <Trash2 className="w-4 h-4" /><span className="hidden 2xl:inline">Delete</span>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>

      <EditComplaintDialog
        complaint={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchComplaints(); }}
        salespeople={salespeople}
      />

      <ViewComplaintDialog
        complaint={viewing}
        onClose={() => setViewing(null)}
        displayId={displayId}
      />
    </div>
  );
}


// One labelled line of the View dialog. Module level so the rows (and the
// photo buttons inside them) are not remounted on every render.
function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[100px_1fr] gap-2 text-sm">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-heda-mute pt-0.5">{label}</span>
      <span className="text-heda-ink break-words whitespace-pre-wrap">{children ?? '—'}</span>
    </div>
  );
}

// ─── View dialog — read-only detail. Simplest possible Dialog with
// labelled rows, keeping in style with the edit dialog shell above.
export function ViewComplaintDialog({ complaint, onClose, displayId }) {
  const [openViewer, viewer] = useImageViewer('Complaint photo');
  const open = !!complaint;
  if (!complaint) return null;
  const photos = photoUrls(complaint.images, complaint.image_urls);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] flex flex-col" data-testid="complaint-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-heda-gold" /> Complaint Details
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-1 space-y-3">
          <Row label="Complaint ID"><span className="font-mono text-[12px]">{displayId(complaint)}</span></Row>
          <Row label="Date">{fmtDate(complaint.created_at)}</Row>
          <Row label="Customer">{complaint.customer_name}</Row>
          <Row label="Mobile">{complaint.mobile || '—'}</Row>
          <Row label="Description">
            <span data-testid="complaint-view-description" className="block leading-relaxed">
              {complaint.description || '—'}
            </span>
          </Row>
          <Row label="Attended By">{complaint.attended_by_name || '—'}</Row>
          <Row label="Status">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${STATUS_CHIP[complaint.status] || STATUS_CHIP.pending}`}>
              {STATUS_LABEL[complaint.status] || 'Pending'}
            </span>
          </Row>
          <Row label="Action Taken">{complaint.action_taken || '—'}</Row>
          <Row label="Remarks">{complaint.remarks || '—'}</Row>
          <Row label="Logged By">{complaint.created_by_name || '—'}</Row>
          <Row label="Photos">
            {(complaint.images || []).length === 0 ? '—' : (
              <div className="flex flex-wrap gap-2">
                {complaint.images.map((filename, i) => (
                  <PhotoThumb
                    key={filename}
                    src={photos[i]}
                    onClick={() => openViewer(photos, i)}
                    label={`Open photo ${i + 1} of ${photos.length}`}
                    testId={`complaint-view-photo-${i}`}
                  />
                ))}
              </div>
            )}
          </Row>
        </div>
        {viewer}
        <DialogFooter className="flex-shrink-0 pt-3 border-t border-heda-line">
          <Button className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" type="button" variant="outline" onClick={onClose} data-testid="complaint-view-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Edit dialog — matches the create-dialog shell used elsewhere ────
export function EditComplaintDialog({ complaint, onClose, onSaved, salespeople }) {
  const [openViewer, viewer] = useImageViewer('Complaint photo');
  const open = !!complaint;
  const [form, setForm] = useState({
    customer_name: '', mobile: '', description: '', attended_by_id: '', attended_by_name: '',
    status: 'pending', action_taken: '', remarks: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);

  useEffect(() => {
    if (complaint) {
      setForm({
        customer_name:    complaint.customer_name    || '',
        mobile:           complaint.mobile           || '',
        description:      complaint.description      || '',
        attended_by_id:   complaint.attended_by_id   || '',
        attended_by_name: complaint.attended_by_name || '',
        status:           complaint.status           || 'pending',
        action_taken:     complaint.action_taken     || '',
        remarks:          complaint.remarks          || '',
      });
      setPendingPhotos([]);
      setPendingPreviews((prev) => {
        prev.forEach((u) => URL.revokeObjectURL(u));
        return [];
      });
    }
  }, [complaint]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onMobile = (e) => setForm((f) => ({ ...f, mobile: (e.target.value || '').replace(/\D/g, '') }));

  const handleAttendedBy = (id) => {
    const sp = salespeople.find((x) => x.id === id);
    setForm((f) => ({ ...f, attended_by_id: id, attended_by_name: sp?.name || '' }));
  };

  const handlePickPhotos = (e) => {
    const chosen = Array.from(e.target.files || []);
    const existingCount = (complaint?.images || []).length;
    const remaining = MAX_SITE_IMAGES - existingCount - pendingPhotos.length;
    if (remaining <= 0) {
      toast.error(`Max ${MAX_SITE_IMAGES} photos per complaint`);
      e.target.value = '';
      return;
    }
    const accepted = [], previews = [];
    for (const f of chosen.slice(0, remaining)) {
      if (!f.type.startsWith('image/')) { toast.error(`${f.name}: not an image`); continue; }
      if (f.size > MAX_IMAGE_BYTES) { toast.error(`${f.name}: exceeds 5 MB`); continue; }
      accepted.push(f);
      previews.push(URL.createObjectURL(f));
    }
    setPendingPhotos((prev) => [...prev, ...accepted]);
    setPendingPreviews((prev) => [...prev, ...previews]);
    e.target.value = '';
  };

  const removePending = (idx) => {
    setPendingPhotos((prev) => prev.filter((_, i) => i !== idx));
    setPendingPreviews((prev) => {
      const removed = prev[idx];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleDeleteExisting = async (filename) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await api.deleteComplaintImage(complaint.id, filename);
      toast.success('Photo removed');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const handleSave = async () => {
    if (!form.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    setSubmitting(true);
    try {
      // Send only what changed: the update is partial on the server, so an
      // untouched field (e.g. an old short mobile number) is left alone.
      const changes = Object.fromEntries(
        Object.entries(form).filter(([k, v]) => v !== (complaint[k] || (k === 'status' ? 'pending' : ''))),
      );
      if (Object.keys(changes).length > 0) await api.updateComplaint(complaint.id, changes);
      if (pendingPhotos.length > 0) {
        try {
          const fd = new FormData();
          pendingPhotos.forEach((f) => fd.append('files', f, f.name));
          const token = localStorage.getItem('heda_token') || '';
          await axios.post(`${API}/complaints/${complaint.id}/images`, fd, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
        } catch (upErr) {
          toast.error(upErr.response?.data?.detail || 'Photos upload failed — changes saved');
        }
      }
      toast.success('Complaint updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update complaint');
    } finally {
      setSubmitting(false);
    }
  };

  if (!complaint) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] flex flex-col" data-testid="complaint-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-heda-gold" /> Edit Complaint
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-3 [&_input]:min-h-11 sm:[&_input]:min-h-10">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Customer Name <span className="text-red-700">*</span></Label>
            <Input className="rounded-xl bg-heda-cream/50" value={form.customer_name} onChange={set('customer_name')} required data-testid="complaint-edit-name" />
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Description</Label>
            <Textarea className="rounded-xl bg-heda-cream/50" rows={4} value={form.description} onChange={set('description')} data-testid="complaint-edit-description" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Mobile</Label>
              <Input className="rounded-xl bg-heda-cream/50" type="tel" inputMode="numeric" value={form.mobile} onChange={onMobile} data-testid="complaint-edit-mobile" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Status</Label>
              <Select value={form.status} onValueChange={setSel('status')}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50" data-testid="complaint-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMPLAINT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Attended By</Label>
            <Select value={form.attended_by_id || undefined} onValueChange={handleAttendedBy}>
              <SelectTrigger className="rounded-xl bg-heda-cream/50" data-testid="complaint-edit-attended"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {salespeople.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Action Taken</Label>
            <Textarea className="rounded-xl bg-heda-cream/50" rows={2} value={form.action_taken} onChange={set('action_taken')} data-testid="complaint-edit-action" />
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Remarks</Label>
            <Textarea className="rounded-xl bg-heda-cream/50" rows={2} value={form.remarks} onChange={set('remarks')} data-testid="complaint-edit-remarks" />
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-heda-gold" /> Photos
            </Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(complaint.images || []).map((filename, i, all) => (
                <div key={filename} className="relative w-16 h-16">
                  <PhotoThumb
                    src={photoUrl(filename, complaint.image_urls)}
                    onClick={() => openViewer(photoUrls(all, complaint.image_urls), i)}
                    label={`Open photo ${i + 1} of ${all.length}`}
                    testId={`complaint-edit-photo-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteExisting(filename)}
                    className="min-h-0 absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full w-4 h-4 flex items-center justify-center"
                    aria-label="Remove"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {pendingPreviews.map((url, idx) => (
                <div key={url} className="relative w-16 h-16 rounded-xl overflow-hidden border border-heda-line bg-heda-cream">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePending(idx)}
                    className="min-h-0 absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full w-4 h-4 flex items-center justify-center"
                    aria-label="Remove"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {((complaint.images || []).length + pendingPhotos.length) < MAX_SITE_IMAGES && (
                <label className="w-16 h-16 rounded-xl border border-dashed border-heda-line bg-heda-cream/50 flex flex-col items-center justify-center cursor-pointer hover:border-heda-gold">
                  <Camera className="w-4 h-4 text-heda-sub" />
                  <span className="text-[11px] text-heda-sub mt-0.5">Add</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handlePickPhotos} />
                </label>
              )}
            </div>
          </div>
        </div>

        {viewer}
        <DialogFooter className="flex-shrink-0 pt-3 border-t border-heda-line gap-2 sm:gap-0">
          <Button className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" type="button" variant="outline" onClick={onClose} disabled={submitting} data-testid="complaint-edit-cancel">Cancel</Button>
          <Button type="button" onClick={handleSave} className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black" disabled={submitting} data-testid="complaint-edit-save">
            {submitting ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Saving…</> : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
